import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { SearchListItem } from '@finance-lab/utils/types'

export type SearchHistory = {
    query: string
    item: SearchListItem
    date: string
}

function isSearchHistoryEntry(entry: unknown): entry is SearchHistory {
    if (!entry || typeof entry !== 'object') return false
    const e = entry as Record<string, unknown>
    if (typeof e.query !== 'string' || typeof e.date !== 'string') return false
    const item = e.item
    if (!item || typeof item !== 'object') return false
    const it = item as Record<string, unknown>
    return typeof it.entityType === 'string' && typeof it.id === 'string'
}

const STORAGE_KEY = 'searchHistory'

export type SearchHistoryContextValue = {
    searchHistory: SearchHistory[]
    setSearchHistory: (searchHistory: SearchHistory[]) => void
    addSearchHistoryEntry: (entry: SearchHistory) => void
}

export const SearchHistoryContext = createContext<SearchHistoryContextValue>({
    searchHistory: [],
    setSearchHistory: () => {},
    addSearchHistoryEntry: () => {},
})

export const SearchHistoryProvider = ({ children }: { children: React.ReactNode }) => {
    const [searchHistory, setSearchHistoryState] = useState<SearchHistory[]>([])

    useEffect(() => {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) return

        try {
            const parsed = JSON.parse(raw) as unknown
            if (Array.isArray(parsed)) {
                const next = parsed.filter(isSearchHistoryEntry)
                setSearchHistoryState(next)
                if (next.length !== parsed.length) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
                }
            }
        } catch (error) {
            console.error('Error parsing search history from localStorage:', error)
        }
    }, [])

    const setSearchHistory = useCallback((next: SearchHistory[]) => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        setSearchHistoryState(next)
    }, [])

    const addSearchHistoryEntry = useCallback((entry: SearchHistory) => {
        setSearchHistoryState((prev) => {
            const key = `${entry.item.entityType}:${entry.item.id}`
            const filtered = prev.filter((e) => `${e.item.entityType}:${e.item.id}` !== key)
            const next = [entry, ...filtered]
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
            return next
        })
    }, [])

    const value = useMemo(
        () => ({ searchHistory, setSearchHistory, addSearchHistoryEntry }),
        [searchHistory, setSearchHistory, addSearchHistoryEntry],
    )

    return <SearchHistoryContext.Provider value={value}>{children}</SearchHistoryContext.Provider>
}
